import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { NotificationService } from '../services/notification.service';
import {
  CODIGO_OPERADOR_INVALIDO, CODIGO_OPERADOR_PIN_TEMPORAL, CODIGO_OPERADOR_VENCIDO, ENCABEZADO_OPERADOR,
  OperadorService, codigoDeOperador,
} from '../operador/operador.service';

/** Para entrar y para saber si se pide PIN no hay sesión de operador que mandar (el servidor las deja pasar sin ella). */
const SIN_SESION = ['/taller/operador/entrar', '/taller/operador/estado'];

/**
 * Dice al servidor qué mecánico tiene la tablet de taller ({@code X-Operador}) y atiende los avisos de que esa sesión ya no sirve.
 *
 * <p>Un 403 con código OPERADOR_* no es un error del trabajo sino de quién lo hace: la pantalla pide el PIN y, si es el mismo
 * mecánico quien vuelve, <b>lo que estaba haciendo se repite solo</b> (guardar un ítem, subir una foto). Si quien vuelve es otro, no
 * se repite: quedaría a nombre de quien no lo hizo. Lo que viaja en segundo plano (la cola offline) no espera: avisa y la cola
 * decide, porque esperar detendría todo lo demás.
 *
 * <p>Va antes del reintento de arranque en frío para ver las marcas de la petición original (X-Skip-Retry).
 */
export const operadorInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.includes(environment.apiUrl)) return next(req);
  const operador = inject(OperadorService);
  const notificacion = inject(NotificationService);
  return intentar(req, next, operador, notificacion, true);
};

function intentar(req: HttpRequest<unknown>, next: HttpHandlerFn, operador: OperadorService, notificacion: NotificationService,
                  puedeRepetir: boolean): Observable<HttpEvent<unknown>> {
  const token = operador.tokenActual();
  const propio = !req.headers.has(ENCABEZADO_OPERADOR) && token !== null && !SIN_SESION.some(r => req.url.includes(r));
  const envio = propio ? req.clone({ setHeaders: { [ENCABEZADO_OPERADOR]: token as string } }) : req;
  const dueno = operador.idActual();

  return next(envio).pipe(catchError((error: HttpErrorResponse) => {
    const codigo = codigoDeOperador(error);
    const llevabaSesion = propio || req.headers.has(ENCABEZADO_OPERADOR);
    if (!codigo || !llevabaSesion) return throwError(() => error);

    // Llevaba la sesión de un mecánico guardado de antes y la tablet ya supo que no pide PIN (apagado, o nadie lo tiene): todo va a
    // nombre de la cuenta, como siempre, así que se repite sin la sesión vieja en vez de fallar o avisar «cambió el mecánico».
    if (propio && puedeRepetir && operador.sinPin()) return intentar(req, next, operador, notificacion, false);

    // La respuesta es de una sesión que ya no es la de la tablet (otro mecánico la tomó mientras tanto): no toca a quien está ahora.
    if (propio && operador.tokenActual() !== token) {
      notificacion.error('Cambió el mecánico de la tablet. Vuelve a hacer este cambio.');
      return throwError(() => error);
    }
    if (codigo === CODIGO_OPERADOR_VENCIDO) operador.alVencer();
    else if (codigo === CODIGO_OPERADOR_INVALIDO) operador.alInvalidarse();
    else if (codigo === CODIGO_OPERADOR_PIN_TEMPORAL) operador.alExigirCambioDePin();
    else return throwError(() => error);

    const enSegundoPlano = req.headers.has('X-Skip-Retry');
    if (enSegundoPlano || !puedeRepetir || !propio || dueno === null) return throwError(() => error);

    return operador.esperarDesbloqueo().pipe(switchMap(id => {
      // Sin nadie: o la tablet resultó no pedir PIN (se repite a nombre de la cuenta) o se cerró la sesión de la cuenta.
      if (id === null) return operador.sinPin() ? intentar(req, next, operador, notificacion, false) : throwError(() => error);
      if (id !== dueno) {
        notificacion.error('Cambió el mecánico de la tablet. Vuelve a hacer este cambio.');
        return throwError(() => error);
      }
      return intentar(req, next, operador, notificacion, false);
    }));
  }));
}
